import fetchClient from 'utils/axiosConfig';
import constants from 'utils/constants';
import axios from 'axios';
import * as types from './appDesignerTypes';

export const getPages = async (eventId, isWebView = false) => {
    if (!eventId) return;
    const url = !isWebView
        ? constants.API.APP_DESIGNER.PAGES.replace(':eventId', eventId)
        : constants.API.APP_DESIGNER_WEB_VIEW.PAGES.replace(
              ':eventId',
              eventId
          );
    const queryParams = {
        params: {
            sortBy: 'createdAt|asc',
        },
    };
    return fetchClient
        .get(url, queryParams)
        .then(({ data }) => ({ success: true, data }))
        .catch(({ message }) => ({ success: false, message }));
};

export const getPageDetailById = (
    eventId,
    pageId,
    isParticipantView = false,
    isAccessedFromCMS = false
) => {
    const queryParams = {
        params: {
            isAccessedFromCMS,
        },
    };
    const url = constants.API.APP_DESIGNER.PAGES.replace(':eventId', eventId);
    return fetchClient.get(
        `${url}${isParticipantView ? `/view` : ''}/${pageId}`,
        queryParams
    );
};

export const getRegistrationDetailsById = ({ eventId, pageId }) => {
    try {
        const url = constants.API.APP_DESIGNER.REGISTRATION_DETAILS.replace(
            ':eventId',
            eventId
        ).replace(':pageId', pageId);
        return fetchClient
            .get(url)
            .then(({ data }) => ({ success: true, userDetails: data }))
            .catch(({ message }) => ({ success: false, message }));
    } catch {
        return { success: false };
    }
};

export const getRegistrationDetailsPreviewById = ({
    eventId,
    formData: formSettings,
}) => {
    const requestData = {
        formSettings,
    };
    try {
        const url =
            constants.API.APP_DESIGNER.REGISTRATION_DETAILS_PREVIEW.replace(
                ':eventId',
                eventId
            );
        return fetchClient
            .post(url, requestData)
            .then(({ data }) => ({ success: true, userDetails: data }))
            .catch(({ message }) => ({ success: false, message }));
    } catch {
        return { success: false };
    }
};

export const savePageAction = (requestData, pageId, eventId) => {
    const url = constants.API.APP_DESIGNER.PAGES.replace(':eventId', eventId);
    if (!pageId) {
        return fetchClient.post(url, requestData);
    }
    return fetchClient.patch(`${url}/${pageId}`, requestData);
};

export const deletePageDetailById = (eventId, pageId) => {
    const url = constants.API.APP_DESIGNER.PAGES.replace(':eventId', eventId);
    return fetchClient.delete(`${url}/${pageId}`);
};

export const getWidgetDetailById = (
    eventId,
    pageId,
    widgetId,
    isParticipantView = false
) => {
    const url = !isParticipantView
        ? constants.API.APP_DESIGNER.WIDGET_BYID.replace(':eventId', eventId)
              .replace(':pageId', pageId)
              .replace(':widgetId', widgetId)
        : constants.API.APP_DESIGNER_WEB_VIEW.WIDGET_BYID.replace(
              ':eventId',
              eventId
          )
              .replace(':pageId', pageId)
              .replace(':widgetId', widgetId);

    return fetchClient.get(url);
};

export const getWidgets = async (
    eventId,
    pageId,
    isWebView,
    parentWidgetId
) => {
    if (!eventId || !pageId) return;
    const url = !isWebView
        ? constants.API.APP_DESIGNER.WIDGETS.replace(
              ':eventId',
              eventId
          ).replace(':pageId', pageId)
        : constants.API.APP_DESIGNER_WEB_VIEW.WIDGETS.replace(
              ':eventId',
              eventId
          ).replace(':pageId', pageId);
    return fetchClient
        .get(!parentWidgetId ? url : `${url}/${parentWidgetId}`)
        .then(({ data }) => ({ success: true, data }))
        .catch(({ message }) => ({ success: false, message }));
};

let cancelToken;
export const saveWidgetAction = (requestData, eventId, pageId, widgetId) => {
    if (typeof cancelToken !== typeof undefined) {
        cancelToken.cancel('Operation canceled due to new request.');
    }
    cancelToken = axios.CancelToken.source();
    const url = constants.API.APP_DESIGNER.WIDGETS.replace(
        ':eventId',
        eventId
    ).replace(':pageId', pageId);
    if (!widgetId) {
        return fetchClient.post(url, requestData, {
            cancelToken: cancelToken.token,
        });
    }
    return fetchClient.patch(`${url}/${widgetId}`, requestData, {
        cancelToken: cancelToken.token,
    });
};

export const reorderWidgetsAction = (requestData, eventId, pageId) => {
    const url = constants.API.APP_DESIGNER.REORDER_WIDGETS.replace(
        ':eventId',
        eventId
    ).replace(':pageId', pageId);
    const data = {
        order: requestData,
    };
    return fetchClient.post(url, data);
};

export const deleteWidgetById = (eventId, pageId, widgetId) => {
    const url = constants.API.APP_DESIGNER.WIDGETS.replace(
        ':eventId',
        eventId
    ).replace(':pageId', pageId);
    return fetchClient.delete(`${url}/${widgetId}`);
};

export const saveWidgetFileAction = (
    eventId,
    widgetId,
    pageId,
    requestData
) => {
    const config = {
        headers: { 'content-type': 'multipart/form-data' },
    };
    const url = constants.API.APP_DESIGNER.WIDGETS.replace(
        ':eventId',
        eventId
    ).replace(':pageId', pageId);
    return fetchClient
        .patch(`${url}/${widgetId}/file`, requestData, config)
        .then(({ data }) => ({ success: true, data }))
        .catch(({ message }) => ({ success: false, message }));
};

export const deleteWidgetFileAction = (eventId, widgetId, pageId) => {
    const url = constants.API.APP_DESIGNER.WIDGETS.replace(
        ':eventId',
        eventId
    ).replace(':pageId', pageId);
    return fetchClient
        .delete(`${url}/${widgetId}/file`)
        .then(({ data }) => ({ success: true, data }))
        .catch(({ message }) => ({ success: false, message }));
};

// Device selection actions
export const setSelectedDevice = (device) => ({
    type: types.SET_SELECTED_DEVICE,
    payload: device,
});

// Theme mode selection actions
export const setSelectedThemeMode = (themeMode) => ({
    type: types.SET_SELECTED_THEME_MODE,
    payload: themeMode,
});

export const resetAppDesigner = () => ({
    type: types.RESET_APP_DESIGNER,
});

// Active widget preview actions
export const setActiveWidgetPreview = (widgetData) => ({
    type: types.SET_ACTIVE_WIDGET_PREVIEW,
    payload: widgetData,
});

export const clearActiveWidgetPreview = () => ({
    type: types.CLEAR_ACTIVE_WIDGET_PREVIEW,
});

export const setNavigationPreview = (items) => ({
    type: types.SET_NAVIGATION_PREVIEW,
    payload: items,
});

export const clearNavigationPreview = () => ({
    type: types.CLEAR_NAVIGATION_PREVIEW,
});

export const setNavigationWidgetsCache = (pageId, widgets) => ({
    type: types.SET_NAVIGATION_WIDGETS_CACHE,
    payload: { pageId, widgets },
});

export const clearNavigationWidgetsCache = () => ({
    type: types.CLEAR_NAVIGATION_WIDGETS_CACHE,
});
